import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./blog.css";

const PAGE_SIZE = 4;

const FALLBACK_CATEGORIES = [
  "Business Loans",
  "Tax Planning",
  "Mutual Funds",
  "Wealth Management",
  "SIP Planning",
];

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).split("T")[0];
  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "2-digit",
    year: "numeric",
  });
}

function estimateReadMins(text = "") {
  const words = String(text).trim().split(/\s+/).filter(Boolean).length;
  return Math.max(3, Math.ceil(words / 180));
}

function stripHtml(html = "") {
  return String(html).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function getCategory(post) {
  if (post.category || post.Category || post.blog_category) {
    return post.category || post.Category || post.blog_category;
  }
  const text = `${post.title || ""} ${stripHtml(post.description || "")}`.toLowerCase();
  if (/(loan|emi|credit|borrow)/.test(text)) return "Business Loans";
  if (/(tax|itr|deduction)/.test(text)) return "Tax Planning";
  if (/(mutual|fund|sip|nav)/.test(text)) return "Mutual Funds";
  if (/(wealth|portfolio|asset)/.test(text)) return "Wealth Management";
  if (/(retire|pension)/.test(text)) return "SIP Planning";
  return "Insights";
}

export default function BlogArticles({ posts = [] }) {
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [activeCategory, setActiveCategory] = useState("");

  const categories = useMemo(() => {
    const map = {};
    posts.forEach((p) => {
      const cat = getCategory(p);
      map[cat] = (map[cat] || 0) + 1;
    });
    const entries = Object.entries(map);
    if (entries.length === 0) {
      return FALLBACK_CATEGORIES.map((name) => ({ name, count: 0 }));
    }
    return entries
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [posts]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return posts.filter((p) => {
      const title = (p.title || "").toLowerCase();
      const desc = stripHtml(p.description || "").toLowerCase();
      const cat = getCategory(p).toLowerCase();
      const matchesQuery =
        !q || title.includes(q) || desc.includes(q) || cat.includes(q);
      const matchesCat =
        !activeCategory || getCategory(p) === activeCategory;
      return matchesQuery && matchesCat;
    });
  }, [posts, query, activeCategory]);

  const shown = filtered.slice(0, visible);
  const canLoadMore = visible < filtered.length;

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (!newsletterEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newsletterEmail)) {
      window.alert("Please enter a valid email address.");
      return;
    }
    window.alert("Thanks for subscribing!");
    setNewsletterEmail("");
  };

  return (
    <section className="blog-articles">
      <div className="blog-articles-inner">
        <div className="blog-articles-main">
          <h2 className="blog-articles-title">Latest Articles</h2>

          {shown.length === 0 ? (
            <p className="blog-empty">No articles found.</p>
          ) : (
            shown.map((data) => {
              const plain = stripHtml(data.description || "");
              const excerpt =
                plain.length > 180 ? `${plain.slice(0, 180)}...` : plain;
              const mins = estimateReadMins(plain);
              const category = getCategory(data);

              return (
                <article key={data.id} className="blog-post-card">
                  <Link
                    to={`/blogdetail/${data.id}`}
                    className="blog-post-thumb"
                  >
                    <img
                      src={`${process.env.REACT_APP_BACKEND_BASE_URL}/uploads/blog/${data.photo}`}
                      alt={data.title || "Blog article"}
                    />
                  </Link>

                  <div className="blog-post-body">
                    <span className="blog-post-cat">{category}</span>
                    <h3>
                      <Link to={`/blogdetail/${data.id}`}>{data.title}</Link>
                    </h3>
                    <div className="blog-post-meta">
                      <span>
                        <i className="fa-solid fa-user" aria-hidden="true" />
                        By GPS Finvest
                      </span>
                      <span>
                        <i className="fa-solid fa-calendar" aria-hidden="true" />
                        {formatDate(data.date)}
                      </span>
                      <span>
                        <i className="fa-solid fa-clock" aria-hidden="true" />
                        {mins} min read
                      </span>
                    </div>
                    <p>{excerpt}</p>
                    <Link to={`/blogdetail/${data.id}`} className="blog-read-more">
                      Read More <span aria-hidden="true">→</span>
                    </Link>
                  </div>
                </article>
              );
            })
          )}

          {canLoadMore && (
            <div className="blog-load-wrap">
              <button
                type="button"
                className="blog-load-more"
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
              >
                Load More Articles
                <i className="fa-solid fa-chevron-down" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>

        <aside className="blog-sidebar">
          <div className="blog-side-card">
            <h3>Search</h3>
            <div className="blog-search">
              <input
                type="search"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setVisible(PAGE_SIZE);
                }}
                placeholder="Search articles, topics or keywords..."
                aria-label="Search articles"
              />
              <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
            </div>
          </div>

          <div className="blog-side-card">
            <h3>Categories</h3>
            <ul className="blog-cats">
              {categories.map((cat) => (
                <li key={cat.name}>
                  <button
                    type="button"
                    className={
                      activeCategory === cat.name ? "is-active" : undefined
                    }
                    onClick={() => {
                      setActiveCategory((prev) =>
                        prev === cat.name ? "" : cat.name
                      );
                      setVisible(PAGE_SIZE);
                    }}
                  >
                    <span>{cat.name}</span>
                    <span className="blog-cat-count">{cat.count}</span>
                  </button>
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="blog-cats-all"
              onClick={() => {
                setActiveCategory("");
                setVisible(PAGE_SIZE);
              }}
            >
              View All Categories <span aria-hidden="true">→</span>
            </button>
          </div>

          <div className="blog-side-card blog-side-help">
            <h3>Have Questions?</h3>
            <p>We&apos;re here to help you.</p>
            <span className="blog-help-icon" aria-hidden="true">
              <i className="fa-solid fa-headset" />
            </span>
            <Link to="/contact" className="blog-side-btn">
              Send Us an Enquiry
            </Link>
          </div>

          <div className="blog-side-card">
            <h3>Newsletter</h3>
            <p>Get our latest updates and articles delivered to your inbox.</p>
            <form onSubmit={handleSubscribe} className="blog-newsletter">
              <input
                type="email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                placeholder="Enter your email address"
                aria-label="Email for newsletter"
              />
              <button type="submit" className="blog-side-btn">
                Subscribe Now
              </button>
            </form>
            <p className="blog-privacy">
              We respect your privacy. Unsubscribe anytime.
            </p>
          </div>
        </aside>
      </div>
    </section>
  );
}
